import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Input,
  Modal,
  Space,
  Typography,
  message,
} from "antd";
import { LinkOutlined, ReloadOutlined } from "@ant-design/icons";
import type { Booking } from "../../../types/booking";
import type { Company } from "../../../types/company";
import type { LeanCustomsDeclaration } from "../../../services/leancustomsService";
import { searchMatchingLeanCustomsDeclarations } from "../../../services/leanCustomsBookingSearch";
import { getLeanCustomsReference } from "../../../utils/bookingUtils";
import { BookingCustomsLeanDeclarationMatchesPanel } from "../../BookingsAdmin/BookingCustomsLeanDeclarationMatchesPanel";
import { supabase } from "../../../lib/supabase";

interface InvoiceCustomsDeclarationLinkProps {
  booking: Booking;
  onLinked: (declarationId: string) => void;
}

function resolveCompany(booking: Booking): Company | null {
  const raw = booking.company_info;
  if (!raw) return null;
  return (Array.isArray(raw) ? raw[0] : raw) as Company;
}

function confirmLink(declarationId: string, bookingId: number): Promise<boolean> {
  return new Promise((resolve) => {
    Modal.confirm({
      title: "Link declaration to this booking?",
      content: (
        <Typography.Paragraph style={{ marginBottom: 0 }}>
          Save declaration <Typography.Text code>{declarationId}</Typography.Text> on
          booking <Typography.Text code>EFF{bookingId}</Typography.Text>? Customs amounts
          on this invoice will reload from LeanCustoms.
        </Typography.Paragraph>
      ),
      okText: "Link to booking",
      cancelText: "Cancel",
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
}

/**
 * Search / paste + link a LeanCustoms declaration when the invoice booking has none.
 */
const InvoiceCustomsDeclarationLink: React.FC<InvoiceCustomsDeclarationLinkProps> = ({
  booking,
  onLinked,
}) => {
  const company = resolveCompany(booking);
  const companyId = company?.id ?? booking.company_id ?? null;
  const orgId = company?.leancustoms_organization_id?.trim() || null;
  const bookingReference = getLeanCustomsReference(booking);

  const [matches, setMatches] = useState<LeanCustomsDeclaration[]>([]);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [manualId, setManualId] = useState("");
  const [searchKey, setSearchKey] = useState(0);

  const runSearch = useCallback(async () => {
    if (!orgId || companyId == null || !booking.id) {
      setMatches([]);
      return;
    }
    setLoading(true);
    try {
      const declarations = await searchMatchingLeanCustomsDeclarations(
        orgId,
        companyId,
        booking.id,
        bookingReference,
        null,
        booking.leancustoms_order_id
      );
      setMatches(declarations);
    } catch (err) {
      console.error(err);
      setMatches([]);
      message.error("Failed to search LeanCustoms declarations");
    } finally {
      setLoading(false);
    }
  }, [
    orgId,
    companyId,
    booking.id,
    booking.leancustoms_order_id,
    bookingReference,
  ]);

  useEffect(() => {
    void runSearch();
  }, [runSearch, searchKey]);

  const linkDeclaration = useCallback(
    async (declarationIdToLink: string) => {
      const raw = declarationIdToLink.trim();
      if (!raw || !booking.id) return;

      const confirmed = await confirmLink(raw, booking.id);
      if (!confirmed) return;

      setLinking(true);
      try {
        const { error } = await supabase
          .from("bookings")
          .update({ leancustoms_declaration_id: raw })
          .eq("id", booking.id);
        if (error) {
          message.error(error.message || "Failed to link declaration");
          return;
        }
        message.success("LeanCustoms declaration linked to this booking");
        onLinked(raw);
      } finally {
        setLinking(false);
      }
    },
    [booking.id, onLinked]
  );

  if (!companyId) {
    return (
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message="No company on this booking — cannot search LeanCustoms"
      />
    );
  }

  if (!orgId) {
    return (
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message="No LeanCustoms organisation linked for this company"
        description={
          <Button
            type="link"
            size="small"
            icon={<LinkOutlined />}
            href={`/admin/companies?id=${companyId}`}
            target="_blank"
            style={{ padding: 0, height: "auto" }}
          >
            Open company settings
          </Button>
        }
      />
    );
  }

  return (
    <Space direction="vertical" size={12} style={{ width: "100%", marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Link a LeanCustoms declaration to auto-fill BTW / invoerrechten.
        </Typography.Text>
        <Button
          size="small"
          icon={<ReloadOutlined />}
          loading={loading}
          onClick={() => setSearchKey((k) => k + 1)}
        >
          Search again
        </Button>
      </div>

      <BookingCustomsLeanDeclarationMatchesPanel
        bookingId={booking.id}
        bookingReference={bookingReference}
        declarations={matches}
        loading={loading}
        linking={linking}
        existingDeclarationId={booking.leancustoms_declaration_id}
        onLinkDeclaration={linkDeclaration}
      />

      <Space.Compact style={{ width: "100%" }}>
        <Input
          size="small"
          placeholder="Or paste declaration ID"
          value={manualId}
          onChange={(e) => setManualId(e.target.value)}
          allowClear
        />
        <Button
          size="small"
          type="primary"
          disabled={!manualId.trim()}
          loading={linking}
          onClick={() => void linkDeclaration(manualId)}
        >
          Link
        </Button>
      </Space.Compact>
    </Space>
  );
};

export default InvoiceCustomsDeclarationLink;
